// FormLogicDrawer — the "Conditional logic" drawer a checkList field's IF
// button (and its canvas banner's "View logic") opens. LIVE edits, the
// FormPagesDrawer idiom: every change lands in the builder state immediately
// through the page's rule handlers, and Done/Close both simply close.
//
// Body: a "N RULES" count row with Collapse all, one FormLogicRuleCard per
// rule (each recursing once for a checkList follow-up's level-2 rules), a
// dashed "+ Add rule", and the designed empty state before any rule exists.
// Deleting a rule confirms first — an ask rule takes its follow-up question
// and everything that question triggers with it (recursive cascade). The
// handlers are fieldId-scoped so nested cards address the CHILD's rules
// through the same functions.

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, Drawer, Icon } from '@/components/common';
import type { BuilderField, BuilderPage } from './utils/builderModel';
import type { ChoiceOption, ElementProperties } from './utils/formElementDefaults';
import { hasFreeRuleValue, type RuleActionSpec } from './utils/triggerOps';
import FormLogicRuleCard from './FormLogicRuleCard';

interface FormLogicDrawerProps {
  open: boolean;
  onClose: () => void;
  /** The checkList whose rules are being edited (null closes render). */
  field: BuilderField | null;
  /** The active page's fields — resolves follow-up elements. */
  fields: BuilderField[];
  pages: BuilderPage[];
  activePageId: string;
  onAddRule: (fieldId: string) => void;
  onSetRuleAnswer: (fieldId: string, ruleId: string, value: string) => void;
  onSetRuleAction: (fieldId: string, ruleId: string, spec: RuleActionSpec) => void;
  onDeleteRule: (fieldId: string, ruleId: string) => void;
  /** Live property writes for a follow-up's inline choices editor. */
  onUpdateFieldProperties: (fieldId: string, properties: ElementProperties) => void;
  onApplyResponseSet: (fieldId: string, options: ChoiceOption[]) => void;
}

const FormLogicDrawer = ({
  open,
  onClose,
  field,
  fields,
  pages,
  activePageId,
  onAddRule,
  onSetRuleAnswer,
  onSetRuleAction,
  onDeleteRule,
  onUpdateFieldProperties,
  onApplyResponseSet,
}: FormLogicDrawerProps) => {
  const { t } = useTranslation();
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(new Set());
  // Deletion target — may belong to the drawer's field OR a nested follow-up.
  const [confirmTarget, setConfirmTarget] = useState<{ fieldId: string; ruleId: string } | null>(
    null,
  );

  const rules = field?.rules ?? [];
  const allCollapsed = rules.length > 0 && rules.every((r) => collapsedIds.has(r.id));
  const confirmRule = confirmTarget
    ? (fields
        .find((f) => f.id === confirmTarget.fieldId)
        ?.rules.find((r) => r.id === confirmTarget.ruleId) ?? null)
    : null;

  const toggleCollapse = (ruleId: string) =>
    setCollapsedIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title={t('forms_builder_logic_title')}
      subtitle={t('forms_builder_logic_subtitle')}
      icon='git-branch'
      className='form-logic'
      footer={
        <>
          <Button onClick={onClose}>{t('button_done')}</Button>
          <Button variant='secondary' onClick={onClose}>
            {t('button_close')}
          </Button>
        </>
      }
    >
      {field && (
        <>
          {rules.length > 0 ? (
            <>
              <div className='form-logic__toolbar'>
                <span className='form-logic__count'>
                  {t('forms_builder_logic_rules_count', { count: rules.length })}
                </span>
                <Button
                  variant='secondary'
                  size='sm'
                  iconStart='chevrons-down-up'
                  onClick={() =>
                    setCollapsedIds(allCollapsed ? new Set() : new Set(rules.map((r) => r.id)))
                  }
                >
                  {t(allCollapsed ? 'forms_builder_expand_all' : 'forms_builder_collapse_all')}
                </Button>
              </div>

              {rules.map((rule, i) => (
                <FormLogicRuleCard
                  key={rule.id}
                  field={field}
                  rule={rule}
                  index={i + 1}
                  depth={1}
                  fields={fields}
                  pages={pages}
                  activePageId={activePageId}
                  collapsedIds={collapsedIds}
                  onToggleCollapse={toggleCollapse}
                  onAddRule={onAddRule}
                  onSetRuleAnswer={onSetRuleAnswer}
                  onSetRuleAction={onSetRuleAction}
                  onRequestDelete={(fieldId, ruleId) => setConfirmTarget({ fieldId, ruleId })}
                  onUpdateFieldProperties={onUpdateFieldProperties}
                  onApplyResponseSet={onApplyResponseSet}
                />
              ))}
            </>
          ) : (
            <div className='form-logic__empty'>
              <span className='form-logic__empty-icon' aria-hidden='true'>
                <Icon name='git-branch' size={22} />
              </span>
              <span className='form-logic__empty-title'>
                {t('forms_builder_logic_empty_title')}
              </span>
              <p className='form-logic__empty-desc'>{t('forms_builder_logic_empty_desc')}</p>
            </div>
          )}

          {/* One rule per answer — with every value taken, a new rule could
           * never pick one, so adding is disabled rather than dead-ending. */}
          <button
            type='button'
            className='form-logic__add'
            disabled={!hasFreeRuleValue(field)}
            title={!hasFreeRuleValue(field) ? t('forms_builder_logic_add_rule_disabled') : undefined}
            onClick={() => onAddRule(field.id)}
          >
            <Icon name='plus' size={15} />
            {t('forms_builder_logic_add_rule')}
          </button>

          {/* Deleting a rule cascades: its follow-up question and everything
           * that question triggers go with it. */}
          <ConfirmModal
            open={Boolean(confirmRule)}
            onClose={() => setConfirmTarget(null)}
            onConfirm={() => {
              if (confirmTarget) onDeleteRule(confirmTarget.fieldId, confirmTarget.ruleId);
              setConfirmTarget(null);
            }}
            tone='danger'
            icon='trash-2'
            title={t('forms_builder_logic_delete_title')}
            description={t(
              confirmRule?.action?.kind === 'ask_a_question'
                ? 'forms_builder_logic_delete_desc_cascade'
                : 'forms_builder_logic_delete_desc',
            )}
            confirmLabel={t('forms_builder_logic_delete_rule')}
            cancelLabel={t('button_cancel')}
            confirmVariant='danger'
          />
        </>
      )}
    </Drawer>
  );
};

export default FormLogicDrawer;
